import React, { useState, useEffect, useCallback } from 'react';
import { getReconcile, getW2List, uploadW2, fetchFileBlobByKey, type ReconcileResponse, type W2Record, type SettlementDetailItem } from '../services/api';
import { FileText, Upload, Loader2, AlertCircle, CheckCircle, AlertTriangle, Banknote, ChevronRight, ChevronDown, ExternalLink } from 'lucide-react';

const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

const currentYear = new Date().getFullYear();
const availableYears = Array.from(
  { length: currentYear - 2019 + 2 },
  (_, i) => currentYear + 1 - i
);

export default function IncomeAudit() {
  const [year, setYear] = useState(() => new Date().getFullYear());
  const [data, setData] = useState<ReconcileResponse | null>(null);
  const [w2List, setW2List] = useState<W2Record[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadYear, setUploadYear] = useState(year);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [reconcileRes, listRes] = await Promise.all([
        getReconcile(year),
        getW2List(),
      ]);
      setData(reconcileRes);
      setW2List(listRes.w2_records || []);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [year]);

  useEffect(() => { load(); }, [load]);

  const handleW2Upload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError(null);
    try {
      await uploadW2(file, uploadYear);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const ledgerTotal = data?.ledger_total ?? 0;
  const w2Box1 = data?.w2_box1 ?? null;
  const status = data?.reconciliation_status ?? null;
  const isStatutory = data?.is_statutory_employee === true;
  const businessExpenses = data?.total_business_expenses ?? 0;
  const monthly = data?.monthly_settlements ?? [];
  const monthMap = Object.fromEntries(monthly.map((m) => [m.month, m.total]));
  const settlementDetails = data?.settlement_details ?? [];
  const [expandedMonth, setExpandedMonth] = useState<number | null>(null);

  const handleViewSettlement = async (item: SettlementDetailItem) => {
    if (!item.fileUrl) return;
    try {
      const blob = await fetchFileBlobByKey(item.fileUrl);
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank', 'noopener');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      window.open(item.fileUrl, '_blank');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Income Audit Tool</h1>
        <div className="flex items-center gap-3">
          <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Tax year</label>
          <select
            value={year}
            onChange={(e) => setYear(parseInt(e.target.value, 10))}
            className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
          >
            {availableYears.map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <div className="flex items-center gap-2">
            <select
              value={uploadYear}
              onChange={(e) => setUploadYear(parseInt(e.target.value, 10))}
              className="rounded-lg border border-gray-300 bg-white px-2 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100"
            >
              {availableYears.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <label className="cursor-pointer rounded-lg bg-emerald-600 px-3 py-2 text-sm font-medium text-white hover:bg-emerald-700">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Upload className="mr-1.5 inline h-4 w-4" /> Upload W2</>}
              <input type="file" accept=".pdf,application/pdf" className="hidden" onChange={handleW2Upload} disabled={uploading} />
            </label>
          </div>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
          <AlertCircle className="h-5 w-5 shrink-0" />
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </div>
      ) : data ? (
        <>
          {/* Income comparison: Ledger Total vs W2 Box 1 */}
          <section className="grid gap-4 md:grid-cols-2">
            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-500 dark:text-gray-400">
                <FileText className="h-4 w-4" /> Ledger total (settlements)
              </div>
              <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-gray-100">
                ${ledgerTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">Sum of settlement income for {year}</p>
            </div>
            <div className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-500 dark:text-gray-400">
                <FileText className="h-4 w-4" /> W2 Box 1 (Wages)
              </div>
              <p className="mt-2 text-3xl font-bold text-gray-900 dark:text-gray-100">
                {w2Box1 != null ? `$${Number(w2Box1).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '—'}
              </p>
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                {data.w2_record?.employer_name ? `From ${data.w2_record.employer_name}` : 'Upload a W2 for this year'}
              </p>
            </div>
          </section>

          {/* Reconciliation status */}
          {status && w2Box1 != null && (
            <div className={`flex items-center gap-3 rounded-xl border p-4 ${
              status === 'MATCHED'
                ? 'border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-900/20'
                : status === 'UNDER_REPORTED'
                  ? 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20'
                  : 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20'
            }`}>
              {status === 'MATCHED' ? (
                <CheckCircle className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
              ) : (
                <AlertTriangle className="h-6 w-6 text-amber-600 dark:text-amber-400" />
              )}
              <div>
                <p className="font-medium text-gray-900 dark:text-gray-100">
                  {status === 'MATCHED' && 'Reconciliation: Ledger matches W2 Box 1.'}
                  {status === 'UNDER_REPORTED' && 'Reconciliation: Ledger is less than W2 (under-reported income or timing).'}
                  {status === 'OVER_REPORTED' && 'Reconciliation: Ledger exceeds W2 Box 1 (over-reported or additional income).'}
                </p>
              </div>
            </div>
          )}

          {/* Statutory banner */}
          {isStatutory && (
            <div className="rounded-xl border border-blue-200 bg-blue-50 p-4 dark:border-blue-800 dark:bg-blue-900/20">
              <p className="font-medium text-blue-900 dark:text-blue-100">
                Statutory Status Detected: Business expenses (${businessExpenses.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}) are valid for Schedule C deduction against W2 wages.
              </p>
              <p className="mt-1 text-sm text-blue-700 dark:text-blue-300">
                Box 13 (Statutory employee) is checked on your W2. OTR Schedule C deductions apply.
              </p>
            </div>
          )}

          {/* Bank sync status: monthly settlement totals + placeholder for bank deposits / Unpaid */}
          <section className="rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
            <h2 className="border-b border-gray-200 px-4 py-3 text-sm font-semibold text-gray-700 dark:border-gray-700 dark:text-gray-300">
              Bank sync status – monthly settlement totals
            </h2>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                <thead>
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400">Month</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400">Settlement total</th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400">Bank deposit</th>
                    <th className="px-4 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => {
                    const total = monthMap[m] ?? 0;
                    const bankDeposit = null;
                    const unpaid = total > 0 && (bankDeposit == null || Number(bankDeposit) < total - 0.01);
                    const isExpanded = expandedMonth === m;
                    const monthSettlements = settlementDetails.filter((s) => s.month === m);
                    return (
                      <React.Fragment key={m}>
                        <tr
                          onClick={() => setExpandedMonth(isExpanded ? null : m)}
                          className={`text-sm cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 ${isExpanded ? 'bg-gray-50 dark:bg-gray-700/50' : ''}`}
                        >
                          <td className="px-4 py-2 text-gray-900 dark:text-gray-100">
                            <span className="inline-flex items-center gap-1">
                              {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                              {MONTHS[m - 1]} {year}
                            </span>
                          </td>
                          <td className="px-4 py-2 text-right tabular-nums text-gray-700 dark:text-gray-300">
                            ${total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </td>
                          <td className="px-4 py-2 text-right tabular-nums text-gray-500 dark:text-gray-400">
                            {bankDeposit != null ? `$${Number(bankDeposit).toLocaleString('en-US', { minimumFractionDigits: 2 })}` : '—'}
                          </td>
                          <td className="px-4 py-2 text-center">
                            {unpaid ? (
                              <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-200">
                                <Banknote className="h-3 w-3" /> Unpaid settlement
                              </span>
                            ) : total > 0 ? (
                              <span className="text-emerald-600 dark:text-emerald-400">Matched</span>
                            ) : (
                              <span className="text-gray-400">—</span>
                            )}
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr>
                            <td colSpan={4} className="px-4 py-0 pb-3 bg-gray-50 dark:bg-gray-800/80">
                              {monthSettlements.length === 0 ? (
                                <p className="py-2 text-sm text-gray-500 dark:text-gray-400">No settlement entries for this month.</p>
                              ) : (
                                <table className="min-w-full text-sm border border-gray-200 dark:border-gray-600 rounded-lg overflow-hidden">
                                  <thead>
                                    <tr className="bg-gray-100 dark:bg-gray-700">
                                      <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-600 dark:text-gray-300">Date</th>
                                      <th className="px-3 py-1.5 text-left text-xs font-medium text-gray-600 dark:text-gray-300">Description</th>
                                      <th className="px-3 py-1.5 text-right text-xs font-medium text-gray-600 dark:text-gray-300">Amount</th>
                                      <th className="px-3 py-1.5 text-center text-xs font-medium text-gray-600 dark:text-gray-300">View</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
                                    {monthSettlements.map((s) => (
                                      <tr key={s.id} className="bg-white dark:bg-gray-800">
                                        <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300">{s.date}</td>
                                        <td className="px-3 py-1.5 text-gray-700 dark:text-gray-300">
                                          {(s.vendor_name || s.description || '—').slice(0, 60)}
                                          {(s.vendor_name || s.description || '').length > 60 ? '…' : ''}
                                        </td>
                                        <td className="px-3 py-1.5 text-right tabular-nums font-medium text-gray-900 dark:text-gray-100">
                                          ${s.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </td>
                                        <td className="px-3 py-1.5 text-center">
                                          {s.fileUrl ? (
                                            <button
                                              type="button"
                                              onClick={(e) => { e.stopPropagation(); handleViewSettlement(s); }}
                                              className="inline-flex items-center gap-1 text-emerald-600 hover:text-emerald-700 dark:text-emerald-400 dark:hover:text-emerald-300"
                                            >
                                              <ExternalLink className="h-3.5 w-3.5" /> View
                                            </button>
                                          ) : (
                                            <span className="text-gray-400">—</span>
                                          )}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <p className="border-t border-gray-200 px-4 py-2 text-xs text-gray-500 dark:border-gray-700 dark:text-gray-400">
              Link bank data to compare deposits to settlement totals and flag Unpaid Settlements.
            </p>
          </section>
        </>
      ) : !loading && !data && !error ? (
        <p className="text-gray-500 dark:text-gray-400">Select a year to load reconciliation.</p>
      ) : null}
    </div>
  );
}
